import React from "react";
import { useRegDetail } from "shared/context/regDetail";
import { useGetFormsListForCouponCode } from "./dataloader";
import * as Yup from "yup";
import Validation from "shared/form/Validation";
import { useCopies } from "shared/hooks/useCopies";
import { Base64Util } from "shared/utils/base64";
import { useSearchParams } from "react-router-dom";
import { MAX_COUPON_CODE_LENGTH, MIN_COUPON_CODE_LENGTH } from "shared/utils/constants";
import { alphaNumeric } from "shared/utils/regex";
import QueryParamKey from "../../../../shared/enums/QueryParamKey";

export function useCouponCodeService() {
  const copies = useCopies();
  const { id: registrationId } = useRegDetail();
  const [couponCode, setCouponCode] = React.useState<string | undefined>();
  const [invalidCouponCode, setInvalidCouponCode] = React.useState<string | null>(null);
  const { data, mutateAsync: getRegForms } = useGetFormsListForCouponCode();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleCouponCodeRemove = React.useCallback(() => {
    setCouponCode(undefined);
    setSearchParams("");
  }, [setSearchParams]);

  React.useEffect(() => {
    const couponCodeKey = searchParams.get(QueryParamKey.COUPON_CODE);
    if (couponCodeKey) {
      const decodedCouponCode = Base64Util.decode(couponCodeKey) || "";
      getRegForms({ params: { registrationId, couponCode: decodedCouponCode } })
        .then(() => {
          setCouponCode(decodedCouponCode);
          setInvalidCouponCode(null);
        })
        .catch(() => setInvalidCouponCode(""));
    }
  }, [getRegForms, registrationId, searchParams]);

  const handleSubmit = React.useCallback(
    ({ couponCode }: { couponCode: string }) => {
      getRegForms({ params: { registrationId, couponCode } })
        .then(() => {
          setCouponCode(couponCode);
          setSearchParams(`${QueryParamKey.COUPON_CODE}=${Base64Util.encode(couponCode)}`);
          setInvalidCouponCode(null);
        })
        .catch(() => setInvalidCouponCode(couponCode));
    },
    [getRegForms, registrationId, setSearchParams]
  );

  const initialValues = React.useMemo(
    () => ({
      couponCode: couponCode || ""
    }),
    [couponCode]
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        couponCode: Validation.textFieldValidation({
          copies,
          required: true,
          min: MIN_COUPON_CODE_LENGTH,
          max: MAX_COUPON_CODE_LENGTH,
          regex: alphaNumeric,
          regexMsg: copies.get("alphanumeric.allowed"),
          minCharactersCopyKey: "minimum.characters.without.spaces",
          maxCharactersCopyKey: "maximum.characters.without.spaces"
        })
      }),
    [copies]
  );

  return {
    handleSubmit,
    initialValues,
    validationSchema,
    regFormList: data?.registrationForms,
    couponCode,
    invalidCouponCode,
    handleCouponCodeRemove
  };
}
